import React from 'react';
import { history } from '../../model/history';
import { voucherCampaignPath } from '../../routes';
import { useMutation } from '@apollo/client';
import { VoucherCampaignFormFields } from './form';
import { graphql } from '../../generated';
import { VoucherCampaign } from '../../generated/graphql';
import { useModal } from '../../components/Modal/PresentedModal';
import { t } from '../../locales/translate';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { handleError } from '../../model/notification';
import { FormForm } from '../../components/Form/FormForm';

const CREATE_VOUCHER_CAMPAIGN = graphql(`
  mutation CreateVoucherCampaign(
    $shopId: ID!
    $title: String!
    $internalTitle: String
    $target: VoucherCampaignTarget!
    $amount: Float!
    $discountType: VoucherCampaignDiscountType!
    $costSplit: Float
    $usage: VoucherCampaignUsage!
  ) {
    createVoucherCampaign(
      input: {
        shopId: $shopId
        title: $title
        internalTitle: $internalTitle
        target: $target
        amount: $amount
        discountType: $discountType
        costSplit: $costSplit
        usage: $usage
      }
    ) {
      voucherCampaign {
        id
      }
    }
  }
`);

export function VoucherCampaignCreate(): JSX.Element {
  const { open, Modal, close } = useModal();

  const [create] = useMutation(CREATE_VOUCHER_CAMPAIGN);

  const { fields, valid, dirty, onSubmit } = useForm<Partial<VoucherCampaign>>({
    model: {
      target: 'PRODUCT',
      discountType: 'ABSOLUTE',
      costSplit: 0,
      usage: 'MULTIPLE',
    },
    onSubmit: async ({ model }) => {
      const resp = await create({
        variables: {
          shopId: model.shopId,
          title: model.title,
          internalTitle: model.internalTitle,
          target: model.target,
          discountType: model.discountType,
          amount: model.amount,
          costSplit: model.costSplit,
          usage: model.usage,
        } as VoucherCampaign,
      });
      const id = resp.data?.createVoucherCampaign?.voucherCampaign?.id;

      close();

      if (id) {
        history.push(voucherCampaignPath(id));
      }
    },
    validations: {
      shopId: 'required',
      title: 'required',
      target: 'required',
      discountType: 'required',
      amount: [
        'required',
        ({ model }) => {
          if (
            model.amount !== undefined &&
            model.discountType === 'PERCENTAGE' &&
            (model.amount < 0 || model.amount > 1)
          ) {
            return [t.page.voucherCampaigns.form.amountPercentageError];
          }
          return [];
        },
      ],
      costSplit: [
        'required',
        ({ model }) => {
          if (model.costSplit !== undefined && (model.costSplit < 0 || model.costSplit > 1)) {
            return [t.page.voucherCampaigns.form.amountPercentageError];
          }
          return [];
        },
      ],
      usage: 'required',
    },
    onSubmitError: handleError,
  });

  return (
    <Modal open={open} title={t.page.voucherCampaigns.form.create}>
      <FormForm onSubmit={onSubmit} onClose={close} canSubmit={dirty && valid}>
        <VoucherCampaignFormFields fields={fields} />
      </FormForm>
    </Modal>
  );
}
